// cg-eligibility.js — Theta II analysis (no dealer list) + Customer lead capture
(function () {
  const $ = (sel) => document.querySelector(sel);
  const form = $('#cgForm');
  const results = $('#cgResults');
  const alertBox = $('#cgAlert');
  const btn = $('#cgSubmit');
  const spinner = $('#cgSpinner');

  if (!form) return; // section not on this page

  // ---------- helpers ----------
  const km = (miles) => miles * 1.60934;
  const toRad = (d) => (d * Math.PI) / 180;
  function haversine(lat1, lon1, lat2, lon2) {
    const R = 3958.7613; // miles
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  async function geocodeZip(zip) {
    const resp = await fetch(`https://api.zippopotam.us/us/${encodeURIComponent(zip)}`);
    if (!resp.ok) throw new Error('ZIP lookup failed');
    const j = await resp.json();
    const place = j.places && j.places[0];
    return { lat: parseFloat(place.latitude), lon: parseFloat(place.longitude) };
  }

  function thetaMatch(text) {
    const blob = (text || '').toLowerCase();
    return /(theta|ksds|knock sensor|connecting rod|bearing|engine failure|stall|fire|seize|gdi)/i.test(blob);
  }

  async function fetchRecalls(make, model, year) {
    if (!make || !model || !year) return [];
    const url = `https://api.nhtsa.gov/recalls/recallsByVehicle?make=${encodeURIComponent(
      make
    )}&model=${encodeURIComponent(model)}&modelYear=${encodeURIComponent(year)}`;
    try {
      const r = await fetch(url);
      const j = await r.json();
      return j.results || [];
    } catch (e) {
      console.warn('NHTSA fetch failed', e);
      return [];
    }
  }

  // ---------- Save lead to Shopify (creates/updates Customer) ----------
  function saveLeadToShopify({
    email, zip, vin, make, model, year, engine, thetaFound, titleStatus
  }) {
    if (!email) return;

    // Tags for segmentation
    const tags = [
      'eligibility-tool',
      'engine-report',
      'theta-focus'
    ];
    if (model && year) tags.push(`vehicle:${String(model).toLowerCase()}-${year}`);
    if (titleStatus) tags.push(`title:${titleStatus === 'CLEAN' ? 'clean' : 'salvage'}`);

    const note = [
      'Eligibility Tool Lead',
      `ZIP: ${zip || ''}`,
      `VIN: ${vin || ''}`,
      `Vehicle: ${[year, make, model, engine].filter(Boolean).join(' ')}`,
      `Theta recall found: ${thetaFound ? 'YES' : 'NO'}`,
      `Title status: ${titleStatus || 'n/a'}`
    ].join('\n');

    // Build a hidden form to POST to /account (Shopify customer form)
    const iframe = document.createElement('iframe');
    iframe.name = 'cg_hidden_iframe';
    iframe.style.display = 'none';

    const f = document.createElement('form');
    f.action = '/account';                  // Shopify will create/update a Customer
    f.method = 'POST';
    f.target = 'cg_hidden_iframe';
    f.style.display = 'none';

    const add = (name, value) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      f.appendChild(input);
    };

    // Required fields for customer creation
    add('form_type', 'create_customer');
    add('utf8', '✓');
    add('customer[email]', email);
    add('customer[accepts_marketing]', 'true');         // optional but useful
    add('customer[note]', note);
    add('customer[tags]', tags.join(', '));

    document.body.appendChild(iframe);
    document.body.appendChild(f);

    try {
      f.submit();
      console.log('[CG] Lead submitted to Shopify Customers:', email);
    } catch (e) {
      console.warn('[CG] Customer form submit failed', e);
    } finally {
      setTimeout(() => {
        try { f.remove(); iframe.remove(); } catch (_) {}
      }, 3000);
    }
  }

  // ---------- title + render ----------
  function titleCheck(vin) {
    if (!vin) return null;
    const last = vin.trim().slice(-1).toUpperCase();
    return ['8', '9', 'S', 'X'].includes(last) ? 'SALVAGE' : 'CLEAN';
  }

  function render({ titleStatus, thetaFound, analysis }) {
    const titleBadge = titleStatus
      ? `<span class="cg-badge ${titleStatus === 'CLEAN' ? 'cg-pass' : 'cg-fail'}">${
          titleStatus === 'CLEAN' ? 'CLEAN TITLE' : 'SALVAGE / REBUILT'
        }</span>`
      : `<span class="cg-badge">TITLE CHECK SKIPPED</span>`;

    const thetaBadge = thetaFound
      ? `<span class="cg-badge cg-theta">YES — Theta II Recall Detected</span>`
      : `<span class="cg-badge">No Theta II Recall Found</span>`;

    results.innerHTML = `
      <h2>Vehicle Eligibility Report</h2>
      <p>${titleBadge} ${thetaBadge}</p>
      <h3>Theta II Engine Recall Analysis</h3>
      <div class="cg-small" style="white-space:pre-wrap">${analysis}</div>
      <a class="cg-badge cg-theta" style="text-decoration:none;display:inline-block;margin-top:10px" href="/pages/the-books" target="_blank">📘 Get Carl's Garage Books</a>
    `;
    results.hidden = false;
    results.scrollIntoView({ behavior: 'smooth' });
  }

  // ---------- main flow ----------
  async function run(e) {
    if (e) e.preventDefault();
    alertBox.hidden = true;
    const fd = new FormData(form);
    const data = Object.fromEntries(fd.entries());

    if (!data.email || !data.zip_code || !/^\d{5}$/.test(data.zip_code)) {
      alertBox.textContent = 'Enter a valid email and 5-digit ZIP.';
      alertBox.hidden = false;
      return;
    }

    btn && (btn.disabled = true);
    spinner && (spinner.hidden = false);
    results.hidden = true;

    try {
      // Make restriction clear
      const make = (data.vehicle_make || '').trim().toLowerCase();
      if (make && !['kia', 'hyundai', 'genesis'].includes(make)) {
        render({
          titleStatus: titleCheck(data.vin),
          thetaFound: false,
          analysis: 'This tool is only for Kia, Hyundai, and Genesis vehicles. Please enter one of those makes to continue.'
        });
        return;
      }

      // (ZIP geocode kept; we no longer list dealers, but may use this later)
      await geocodeZip(data.zip_code).catch(() => ({}));

      // Recall analysis
      const recalls = await fetchRecalls(
        data.vehicle_make || '',
        data.vehicle_model || '',
        data.vehicle_year || ''
      );
      const theta = (recalls || []).filter((r) =>
        thetaMatch([r.Component, r.Summary, r.Consequence, r.Notes, r.Remedy].join(' '))
      );

      const vline = [data.vehicle_year, data.vehicle_make, data.vehicle_model, data.vehicle_engine]
        .filter(Boolean)
        .join(' ');
      const lines = [];
      lines.push(`Vehicle: ${vline || 'Unknown'}`);
      if (theta.length) {
        lines.push(`⚠️ Found ${theta.length} Theta II engine-related recall(s):`);
        lines.push(
          '<ul>' +
            theta.slice(0, 3).map((r) => {
              const id = r.NHTSACampaignNumber || 'NHTSA Campaign';
              const date = r.ReportReceivedDate || '';
              const summary = (r.Summary || '').replace(/\s+/g, ' ').trim();
              const short = summary.length > 140 ? summary.slice(0, 140) + '…' : summary;
              return `<li>[${id}] ${r.Component || 'Engine'}${date ? ` (${date})` : ''}: ${short}</li>`;
            }).join('') +
          '</ul>'
        );
      } else if (recalls.length) {
        lines.push('Other recalls exist for this vehicle, but none flagged as Theta II-related. Ask the dealer to verify KSDS/Theta II coverage by VIN.');
      } else {
        lines.push('No recalls found. You may still qualify under KSDS/Theta II based on symptoms and build date.');
      }
      lines.push("Action: Book a recall diagnostic, reference Theta II coverage, and get the Carl's Garage books to guide you.");

      // Compute title once so we can reuse for tagging
      const tStatus = titleCheck(data.vin);

      // Show the report
      render({
        titleStatus: tStatus,
        thetaFound: theta.length > 0,
        analysis: lines.join('\n')
      });

      // Save/email → Shopify Customers
      saveLeadToShopify({
        email: data.email,
        zip: data.zip_code,
        vin: data.vin,
        make: data.vehicle_make,
        model: data.vehicle_model,
        year: data.vehicle_year,
        engine: data.vehicle_engine,
        thetaFound: theta.length > 0,
        titleStatus: tStatus
      });

    } catch (err) {
      console.error(err);
      alertBox.textContent = 'Something went wrong. Please try again.';
      alertBox.hidden = false;
    } finally {
      btn && (btn.disabled = false);
      spinner && (spinner.hidden = true);
    }
  }

  // Bind
  form.addEventListener('submit', run);
  const button = document.getElementById('cgSubmit');
  if (button) button.addEventListener('click', run);
})();